import React, { useState } from 'react'
import { listDepartments } from '../services/departments'
import { connectDepartment, switchToDepartment } from '../services/departmentSwitch'

const DIRECTORY_URL = import.meta.env.VITE_DIRECTORY_URL

function ServerSetup({ onConnected }) {
  const [code, setCode] = useState('')
  const [directUrl, setDirectUrl] = useState('')
  const [useDirectUrl, setUseDirectUrl] = useState(false)
  const [status, setStatus] = useState(null) // { type: 'error'|'success', text, deptName? }
  const [loading, setLoading] = useState(false)
  // Departments already on this device, when adding another (see departments.js)
  const [remembered] = useState(() => listDepartments())

  const handleConnect = async () => {
    setStatus(null)
    setLoading(true)

    try {
      let serverUrl, deptName, deptCode

      if (useDirectUrl) {
        // Direct URL mode
        const trimmed = directUrl.trim().replace(/\/+$/, '')
        if (!trimmed) {
          setStatus({ type: 'error', text: 'Please enter a server URL.' })
          setLoading(false)
          return
        }
        // App Transport Security is enforced on iOS — a plain http:// server
        // would fail every request with an opaque network error, so reject it
        // here with an explanation instead.
        const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
        if (!/^https:\/\//i.test(withScheme)) {
          setStatus({ type: 'error', text: 'Server URL must start with https://' })
          setLoading(false)
          return
        }
        serverUrl = withScheme
      } else {
        // Department code mode — look up in directory
        const trimmedCode = code.trim().toLowerCase()
        if (!trimmedCode) {
          setStatus({ type: 'error', text: 'Please enter a department code.' })
          setLoading(false)
          return
        }

        if (!DIRECTORY_URL) {
          setStatus({ type: 'error', text: 'Directory URL not configured. Use direct URL instead.' })
          setLoading(false)
          return
        }

        let directory
        try {
          const res = await fetch(DIRECTORY_URL, { cache: 'no-store' })
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          directory = await res.json()
        } catch (e) {
          setStatus({ type: 'error', text: 'Could not reach the department directory. Try again later.' })
          setLoading(false)
          return
        }

        const entry = directory[trimmedCode]
        if (!entry) {
          setStatus({ type: 'error', text: 'Department code not recognized.' })
          setLoading(false)
          return
        }

        serverUrl = entry.url.replace(/\/+$/, '')
        deptName = entry.name
        deptCode = trimmedCode
      }

      // Validate server is reachable
      try {
        const healthRes = await fetch(`${serverUrl}/api/health`, {
          signal: AbortSignal.timeout(10000),
          cache: 'no-store'
        })
        if (!healthRes.ok) throw new Error(`HTTP ${healthRes.status}`)
        const health = await healthRes.json()
        if (health.status !== 'ok') throw new Error('Unexpected response')
        // Use department name from server if available
        if (!deptName && health.departmentName) deptName = health.departmentName
        if (health.departmentCode) deptCode = health.departmentCode
      } catch (e) {
        setStatus({ type: 'error', text: 'Server is not responding — try again later.' })
        setLoading(false)
        return
      }

      // Success — store and proceed
      // Remembered alongside any departments already here. Reconnecting to
      // one of those brings its sign-in back.
      await connectDepartment({ url: serverUrl, name: deptName, code: deptCode })
      setStatus({ type: 'success', text: deptName ? `Connected to ${deptName}` : 'Connected successfully', deptName })

      // Brief delay to show success before transitioning
      setTimeout(() => onConnected(), 800)
    } catch (e) {
      setStatus({ type: 'error', text: 'An unexpected error occurred. Please try again.' })
    }

    setLoading(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) handleConnect()
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center safe-screen">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/images/scenelight-logo.png" alt="Scenelight" className="w-28 h-28 mx-auto" />
          <h1 className="text-4xl text-white mt-4 tracking-widest" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>SCENELIGHT</h1>
          <p className="text-slate-400 mt-2">Fire Department Operations Platform</p>
        </div>

        {/* Setup card */}
        <div className="card">
          <h2 className="text-xl font-semibold text-white mb-2 text-center">
            Connect to Your Department
          </h2>
          <p className="text-slate-400 text-sm text-center mb-6">
            {useDirectUrl
              ? 'Enter your department server URL.'
              : 'Enter the code provided by your department.'}
          </p>

          {/* Status message */}
          {status && (
            <div className={`mb-4 p-3 rounded-lg text-sm text-center ${
              status.type === 'success'
                ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                : 'bg-red-500/10 border border-red-500/30 text-red-400'
            }`}>
              {status.text}
            </div>
          )}

          {useDirectUrl ? (
            <div className="mb-4">
              <label className="block text-sm text-slate-400 mb-1">Server URL</label>
              <input
                type="url"
                value={directUrl}
                onChange={(e) => setDirectUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                placeholder="https://portal.example.org"
                autoFocus
                autoCapitalize="none"
                autoCorrect="off"
              />
            </div>
          ) : (
            <div className="mb-4">
              <label className="block text-sm text-slate-400 mb-1">Department Code</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                placeholder="Enter your department code"
                enterKeyHint="go"
                autoCapitalize="none"
                autoCorrect="off"
              />
            </div>
          )}

          <button
            onClick={handleConnect}
            disabled={loading}
            className="w-full btn btn-primary py-3 flex items-center justify-center gap-3 disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Connecting...</span>
              </>
            ) : (
              <span>Connect</span>
            )}
          </button>

          {/* Toggle between code and direct URL */}
          <button
            onClick={() => { setUseDirectUrl(!useDirectUrl); setStatus(null) }}
            className="w-full mt-4 text-center text-sm text-slate-500 hover:text-slate-300 transition-colors"
          >
            {useDirectUrl ? 'Use department code instead' : 'Use server URL instead'}
          </button>
        </div>

        {/* Adding a department: a way back to the ones already here */}
        {remembered.length > 0 && (
          <div className="mt-4 space-y-2">
            {remembered.map(d => (
              <button
                key={d.url}
                onClick={() => switchToDepartment(d.url)}
                className="w-full text-center text-sm text-slate-400 hover:text-slate-200 transition-colors"
              >
                Back to {d.name || d.url.replace(/^https?:\/\//, '')}
              </button>
            ))}
          </div>
        )}

        {/* Footer */}
        <p className="mt-4 text-center text-xs text-slate-600">
          &copy; 2026 Scenelight
        </p>
      </div>
    </div>
  )
}

export default ServerSetup
